/** Shared carousel header (divider + title + prev/next arrows). Presentational. */
import type { ReactNode } from 'react'
import { CircleButton, IconArrowRight, cn } from '@mc/educator-design-system'

export interface CarouselHeaderProps {
  title: ReactNode
  prevLabel: string
  nextLabel: string
  onNav: (direction: 'left' | 'right') => void
  className?: string
  dividerClassName?: string
  titleClassName?: string
  rowClassName?: string
  /** aria-controls for both arrows when they sit outside the carousel region. */
  controlsId?: string
  canGoPrev?: boolean
  canGoNext?: boolean
}

export const CarouselHeader = ({
  title,
  prevLabel,
  nextLabel,
  onNav,
  className,
  dividerClassName = 'border-edu-stroke-default',
  titleClassName = 'text-[20px] font-semibold leading-[1.2] md:text-title-3 md:leading-title-3',
  rowClassName = 'items-center',
  controlsId,
  canGoPrev = true,
  canGoNext = true,
}: CarouselHeaderProps) => (
  <header className={cn('flex flex-col gap-3', className)}>
    <div className={cn('border-t', dividerClassName)} />
    <div className={cn('flex justify-between gap-4 md:min-h-12', rowClassName)}>
      <h2 className={cn('m-0', titleClassName)}>{title}</h2>
      <div className='flex shrink-0 items-center gap-4 md:gap-3'>
        <CircleButton
          kind='ghost'
          aria-label={prevLabel}
          aria-controls={controlsId}
          icon={<IconArrowRight className='rotate-180' />}
          className='md:h-12 md:w-12'
          disabled={!canGoPrev}
          onClick={() => onNav('left')}
        />
        <CircleButton
          kind='ghost'
          aria-label={nextLabel}
          aria-controls={controlsId}
          icon={<IconArrowRight />}
          className='md:h-12 md:w-12'
          disabled={!canGoNext}
          onClick={() => onNav('right')}
        />
      </div>
    </div>
  </header>
)
